import React, {
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useTranslation } from "react-i18next";

import { cn } from "~/lib/utils";
import { Input } from "./input";
import { RegisterButton } from "./register-button";

type ValueByType<T> = T extends "number" | "decimal"
  ? number
  : T extends "password"
    ? string
    : string;

const INPUT_TYPES = {
  password: "password",
  number: "number",
  decimal: "decimal",
  text: "text",
} as const;

type InputType = keyof typeof INPUT_TYPES;

export interface InputRef {
  clear: () => void;
}

const formatNumberInput = (inputValue: string) => {
  if (inputValue === "") return "0.00";
  const numericValue = parseInt(inputValue, 10) || 0;
  return (numericValue / 100).toFixed(2);
};

const useKeypadInput = () => {
  const [input, setInput] = useState<string>();

  const setValue = (value: string) => {
    setInput(value);
  };

  const getValue = () => {
    return input;
  };

  return {
    setValue,
    getValue,
  };
};

interface KeypadInputProps<T extends InputType> {
  value?: ValueByType<T>;
  className?: string;
  min?: number;
  max?: number;
  maxButton?: boolean;
  onChange: (value: ValueByType<T>) => void;
  onBlur?: () => void;
  type: T;
  ref?: React.RefObject<InputRef>;
  disabled?: boolean;
  label?: string;
  description?: string;
  icon?: React.ReactNode;
}

const KeypadInputField = ({
  min,
  disabled,
  type,
  value,
}: {
  min?: number;
  disabled?: boolean;
  type: InputType;
  value: string;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <div className="text-4xl text-center">
      <Input
        min={min}
        disabled={disabled}
        readOnly
        autoComplete="no-lastpass"
        ref={inputRef}
        className="text-center text-3xl focus-visible:ring-0 focus-visible:ring-offset-0"
        type={type === "password" ? "password" : "text"}
        value={type === "decimal" ? formatNumberInput(value) : value}
        placeholder={type === "decimal" ? "0.00" : undefined}
      />
    </div>
  );
};

const KeypadInput = <T extends InputType>({
  value,
  onChange,
  onBlur,
  type,
  ref,
  disabled,
  min,
  label,
  icon,
  description,
  max,
  maxButton,
  className,
}: KeypadInputProps<T>) => {
  const initialFormattedValue =
    type === "decimal" && value !== undefined
      ? (+value * 100).toString()
      : (value?.toString() ?? "");

  const [input, setInput] = useState<string>(initialFormattedValue);
  const [isFirstInput, setIsFirstInput] = useState(true);
  const { t } = useTranslation("common");

  const getUnformattedValue = useCallback((inputValue: string) => {
    return parseInt(inputValue, 10) || 0;
  }, []);

  const formatInput = useCallback(
    (inputValue: string) => {
      if (type === "decimal") {
        return formatNumberInput(inputValue);
      }
      return inputValue;
    },
    [type],
  );

  const isValidValue = useCallback(
    (newValue: string) => {
      const parsedValue = parseFloat(formatInput(newValue));
      if (min !== undefined && parsedValue < min) {
        return false;
      }
      if (max !== undefined && parsedValue > max) {
        return false;
      }
      return true;
    },
    [formatInput, min, max],
  );

  const handleKeyPress = useCallback(
    (value: string) => {
      if (disabled) return;
      if (value === "Backspace") {
        setInput((prev) => prev.slice(0, -1));
      } else if (value === "Clear") {
        setInput("");
        setIsFirstInput(true);
      } else if (value === "-" && (type === "decimal" || type === "number")) {
        if (isFirstInput && !input.includes("-")) {
          setInput("-");
          setIsFirstInput(false);
        }
      } else if (/^[0-9]$/.test(value)) {
        const newInput = isFirstInput ? value : input + value;
        if (isValidValue(newInput)) {
          setInput(newInput);
          setIsFirstInput(false);
        }
      }
    },
    [input, isFirstInput, isValidValue, type, disabled],
  );

  const handleSubmit = useCallback(() => {
    const unformattedValue = getUnformattedValue(input);
    const parsedValue = unformattedValue;

    if (min !== undefined && parsedValue < min) {
      setInput(min.toString());
      onChange(min as ValueByType<T>);
    } else {
      if (type === "decimal") {
        onChange((parsedValue / 100) as ValueByType<T>);
      } else if (type === "number") {
        onChange(parsedValue as ValueByType<T>);
      } else {
        onChange(formatInput(input) as ValueByType<T>);
      }
    }

    if (onBlur) {
      onBlur();
    }

    setInput("");
    setIsFirstInput(true);
  }, [input, formatInput, getUnformattedValue, min, onBlur, onChange, type]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const validKeys = "0123456789-";
      if (validKeys.includes(e.key)) {
        handleKeyPress(e.key);
      } else if (e.key === "Enter") {
        setTimeout(() => {
          handleSubmit();
        }, 0);
      } else if (e.key === "Backspace") {
        handleKeyPress("Backspace");
      }
    },
    [handleKeyPress, handleSubmit],
  );

  useImperativeHandle(ref, () => ({
    clear: () => {
      setInput("");
      setIsFirstInput(true);
    },
  }));

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div className="flex flex-col gap-4">
      <div
        className={cn(
          "bg-gray-800 p-6 rounded-lg flex flex-col gap-2",
          className,
        )}
      >
        {icon && <div className="flex items-center justify-center">{icon}</div>}
        {label && <div className="text-center text-white text-lg">{label}</div>}
        {description && (
          <span className="text-gray-200 text-center text-sm">
            {description}
          </span>
        )}

        <KeypadInputField
          value={input}
          disabled={disabled}
          min={min}
          type={type}
        />

        {max && maxButton && (
          <RegisterButton
            variant="warning"
            className="rounded-lg text-2xl flex-1"
            onPointerDown={() => {
              setInput(max.toString());
              setIsFirstInput(false);
            }}
          >
            {t("full_amount")}
          </RegisterButton>
        )}

        <div className="flex flex-row gap-2">
          <div className="grid grid-cols-3 gap-2 flex-1">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9", "-", "0", ""].map(
              (key) => (
                <RegisterButton
                  key={key}
                  variant="secondary"
                  onPointerDown={() => handleKeyPress(key)}
                  className="rounded-lg text-2xl h-18"
                >
                  {key}
                </RegisterButton>
              ),
            )}
          </div>
          <div className="w-1/5 flex flex-col gap-2">
            <RegisterButton
              variant="destructive"
              onPointerDown={() => handleKeyPress("Backspace")}
              className="rounded-lg text-2xl flex-1"
            >
              <PlayArrowIcon fontSize="medium" className="rotate-180" />
            </RegisterButton>
            <RegisterButton
              variant="warning"
              onPointerDown={() => handleKeyPress("Clear")}
              className="text-white rounded-lg text-2xl h-18"
            >
              C
            </RegisterButton>
            <RegisterButton
              variant="success"
              onPointerDown={handleSubmit}
              className="rounded-lg text-2xl h-18"
            >
              {t("ok")}
            </RegisterButton>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrap the component with React.memo
export const MemoizedKeypadInput = memo(KeypadInput);
